import React, {useEffect, useContext, useState, useRef} from 'react';
import Stack from 'react-bootstrap/Stack';
import Button from 'react-bootstrap/Button';
import { useNavigate } from "react-router-dom";
import { FormContext } from 'context/FormContext';
import { sendSimulationData, getBatteryTypes, getStorageConfigList } from 'services/simulatorApiProxy';
// import ImageLoading from 'assets/images/loading.gif';
import BackgroundDefault from 'assets/images/produkte-e3dc-2026.png';
import UserCircleCheckIcon from 'assets/images/UserCircleCheck.svg';
import LightningIcon from 'assets/images/Lightning.svg';
import PartnerMap from 'components/PartnerMap/PartnerMap';

const PV_MODULE_GEN = 400; // 1 PV Module generates 400W = 0.4 kWp
const PV_MODULE_AREA = 1.85; // 1 PV Module = 1,85m²

const SimulationRequest = () => {

    const { formValues, setFormValues, setResultValues, setBatteryTypes, setStorageConfiguration, partnerLocations, isPartnerLocationsLoading } = useContext(FormContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [progress, setProgress] = useState(0);
    const [selectedPartner, setSelectedPartner] = useState(formValues.SelectedPartner || null);
    const [partnerChoice, setPartnerChoice] = useState(null);
    const didFetch = useRef(false);
    const autoStartWithExistingPartner = useRef(!!formValues.FachpartnerId);

    const parseDirection = (direction) => {
        if (direction === 'Nord') {
            return 180;
        }
        if (direction === 'Nordost') {
            return -135;
        }
        if (direction === 'Ost') {
            return -90;
        }
        if (direction === 'Südost') {
            return -45;
        }
        if (direction === 'Süd') {
            return 0;
        }
        if (direction === 'Südwest') {
            return 45;
        }
        if (direction === 'West') {
            return 90;
        }
        if (direction === 'Nordwest') {
            return 135;
        }
        return 0;
    }

    const prepareFormData = (values = formValues) => {

        let data = {
            lat: values.Address.LatLng.lat,
            lng: values.Address.LatLng.lng,
            postcode: values.Address.Zip,
            profiletype: "standardLoadProfile",
            powersource: values.SkipRoof ? "AC" : "DC",
            save: 1
        };

        // yearconsumption
        var yearconsumption_kWh = values.AnnualConsumption;
        if (values.energyUseHeatingPump == 1) {
            yearconsumption_kWh += parseInt(values.HeatingPumpConsumption);
        }
        if (values.Cars.length > 0) {
            yearconsumption_kWh += values.Cars.reduce((sum, value) => {
                const consumptionPer100km =
                    value === 10000 ? 18 :
                    value === 15000 ? 20 :
                    value === 30000 ? 21 : 0;

                return sum + (value * consumptionPer100km) / 100;
            }, 0);
        }
       
        data.yearconsumption = parseInt(yearconsumption_kWh);

        // roof(s)
        data.pvmodules = values.Roof.Zones.map(zone => {
            return {                
                "peakpvpower": Math.floor(zone.SquareMeterUsable / PV_MODULE_AREA) * PV_MODULE_GEN,
                "roofslope": zone.Tilt,
                "roofdirection": parseDirection(zone.Direction)
            }
        });

        if (values.energyUseHeatingPump || values.Cars.length > 0) {
            data.details = 1;
        }

        if (values.energyUseHeatingPump) {                        
            data.yearheatconsumption = values.HeatingPumpConsumption;  // kWh
            data.heatpumptype = 1; // 1 - Geothermal heat pump, 2 - Air heat pump
            data.maxpowerheatpump = 3800; // leistungsaufnahme der wärmepumpe ~3800 watt
        }

        if (values.Cars.length > 0) {
            data.emobility = {
                chargingmode: 1 // 1 - Charging only from solar (PV), 2 - Charging only from grid, 3 - Charging from solar (PV) and battery without grid, 4 - Charging from solar (PV) and battery supported by grid, 5 - Charging from solar (PV) and supported by grid
            }
            data.emobility.ecars = values.Cars.map(value => {
                const capacity = value === 10000
                    ? 40
                    : value === 15000
                        ? 70
                        : value === 30000 ? 95 : 0;
                const consumption = value === 10000
                    ? 18
                    : value === 15000
                        ? 20
                        : value === 30000 ? 21 : 0;
                return {
                    ecarbatterycapacity: capacity * 1000, // in Wh
                    ecarconsumption: consumption, // (Wh/100km)
                    ecardistanceinyear: value, // km
                    chargingdays: [
                        "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"
                    ]
                }
            });
            data.emobility.wallboxes = [
                {
                    ecarratedpowerstation: 0
                }
            ]
        }

        if (values.forceSimulationConfig && values.storageConfiguration) {
            data.forcesimulationconfig = {
                storagetype: values.storageConfiguration.series,
                battcapacity: values.storageConfiguration.battery.capacity,
            }
            // reset immediately
            setFormValues(prev => ({
                ...prev,
                forceSimulationConfig: false
            }));
        }

        data.configuratorConfig = JSON.parse(JSON.stringify(values));

        // remove heating pump values when no heating pump is selected
        if (values.energyUseHeatingPump == 0) {
            delete data.configuratorConfig.UsableSquareMeter;
            delete data.configuratorConfig.HeatingPumpConsumption;
            delete data.configuratorConfig.ConstructionYear;
            delete data.configuratorConfig.RenovationYear;
            delete data.configuratorConfig.EnergyRenovation;
        }

        return data;
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        if (!isLoading) return;

        const duration = 30000; // 30 seconds
        const intervalTime = 100; // update every 100ms
        const maxProgressBeforeResult = 95;
        const increment = maxProgressBeforeResult / (duration / intervalTime);

        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + increment, maxProgressBeforeResult));
        }, intervalTime);

        return () => clearInterval(interval); // cleanup on unmount
    }, [isLoading]);

    useEffect(() => {
        if (autoStartWithExistingPartner.current && formValues.FachpartnerId) {
            fetchData(formValues);
        }
    }, []);

    const backgroundStyle = {
        background: `url(${BackgroundDefault}) no-repeat`,
        backgroundSize: '100%',
        backgroundPosition: 'right bottom',
    };

    const hasValidAddress = (
        formValues.Address &&
        formValues.Address.LatLng &&
        formValues.Address.LatLng.lat &&
        formValues.Address.LatLng.lng
    );

    const handlePartnerChoiceChange = (choice) => {
        if (isLoading) return;

        setPartnerChoice(choice);
        if (choice === 'skip') {
            const nextFormValues = {
                ...formValues,
                FachpartnerId: null,
                SelectedPartner: null,
                ContinueWithoutPartner: true
            };

            setSelectedPartner(null);
            setFormValues(nextFormValues);
            fetchData(nextFormValues);
        } else {
            setFormValues(prev => ({
                ...prev,
                ContinueWithoutPartner: false
            }));
        }
    };

    const handleBackToPartnerDecision = () => {
        if (isLoading) return;

        setPartnerChoice(null);
        setSelectedPartner(null);
        setFormValues(prev => ({
            ...prev,
            FachpartnerId: null,
            SelectedPartner: null,
            ContinueWithoutPartner: false
        }));
    };

    const handlePartnerSelect = (partner) => {
        if (isLoading) return;

        setSelectedPartner(partner);
        setFormValues(prev => ({
            ...prev,
            FachpartnerId: partner ? partner.id : null,
            SelectedPartner: partner,
            ContinueWithoutPartner: false
        }));

        if (partner) {
            setPartnerChoice('select');
        }
    };

    const fetchData = async (values) => {
        if (didFetch.current) return;
        didFetch.current = true;
        setIsLoading(true);
        setError(false);
        setProgress(0);

        try {
            const simulationData = prepareFormData(values);
            const batteryTypesRequest = getBatteryTypes();
            const storageConfigRequest = getStorageConfigList();

            const [result, batteryTypes, storageConfig] = await Promise.all([
                sendSimulationData(simulationData),
                batteryTypesRequest,
                storageConfigRequest
            ]);  
            
            setProgress(100);
            await sleep(400);

            // remove old product
            if (storageConfig.PROSERIE) {
                delete storageConfig.PROSERIE;
            }
            setBatteryTypes(batteryTypes);
            setStorageConfiguration(storageConfig);
            setResultValues(result);                
        
            navigate('/SimulationResult');
        } catch (error) {
            console.error('Error during request:', error);
            setError(true);
            didFetch.current = false;
        } finally {
            setIsLoading(false); // Set loading state to false after the request
        }
    };

    const handleContinue = () => {
        const nextFormValues = {
            ...formValues,
            FachpartnerId: partnerChoice === 'select' && selectedPartner ? selectedPartner.id : null,
            SelectedPartner: partnerChoice === 'select' ? selectedPartner : null,
            ContinueWithoutPartner: partnerChoice === 'skip'
        };

        setFormValues(nextFormValues);
        fetchData(nextFormValues);
    };

    const hasExistingFachpartner = autoStartWithExistingPartner.current;
    const needsPartnerSelection = partnerChoice === 'select' && !selectedPartner && !formValues.FachpartnerId;
    const shouldShowPartnerSelection = hasValidAddress && !hasExistingFachpartner;

    return (
        <Stack gap={4} className={`ContainerPaddingSmall text-center simulation-request-container ${isLoading ? 'simulation-request-container-loading' : ''}`} style={backgroundStyle}>
            <div>
                {isLoading ? (
                    <>
                        <h1>Nur noch wenige Sekunden...</h1>
                        <div className="simulation-request-subtitle">Ihre Konfiguration wird finalisiert – gleich sehen Sie Ihr persönliches Ergebnis</div>
                    </>
                ) : (
                    <>
                        <h1>Partner in Ihrer Nähe auswählen? <span className="simulation-request-optional">(optional)</span></h1>
                        <div className="simulation-request-subtitle">Unsere Experten beraten Sie gerne unverbindlich zu Ihrem Ergebnis.</div>
                    </>
                )}
            </div>
            <div className="d-flex justify-content-center align-items-center flex-column simulation-request-content">                

                {isLoading ? (
                    // <img src={ImageLoading} className="LoadingAnimation" alt="Loading..." width="150" height="150" />
                    <div style={{width: '200px'}}>
                        <div className="progress LoadingAnimation">
                            <div
                                className="progress-bar text-bg-e3dc progress-bar-striped progress-bar-animated" 
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>                            
                        </div>{Math.round(progress)}%
                    </div>
                ) : error ? (
                    <div className="text-danger">Es gab ein Problem mit der Anfrage. Bitte versuchen Sie es später noch einmal.</div>
                ) : shouldShowPartnerSelection && !partnerChoice ? (
                    <div className="partner-decision-cards">
                        <button
                            type="button"
                            className="partner-decision-card partner-decision-card-recommended"
                            onClick={() => handlePartnerChoiceChange('select')}
                            disabled={isLoading}
                        >
                            <span className="partner-decision-icon" aria-hidden="true">
                                <img src={UserCircleCheckIcon} alt="" />
                            </span>
                            <span className="partner-decision-body">
                                <span className="partner-decision-title">Partner selbst auswählen</span>
                                <span className="partner-decision-text">Ich sehe alle verfügbaren Partner in meiner Nähe und wähle den passenden für mich aus.</span>
                                <span className="partner-decision-link">Partner auswählen <span aria-hidden="true">→</span></span>
                            </span>
                            <span className="partner-decision-check" aria-hidden="true">✓</span>
                            {/* <span className="partner-decision-badge"><span className="partner-decision-badge-check" aria-hidden="true">✓</span>Empfohlen von unseren Experten</span> */}
                        </button>
                        <button
                            type="button"
                            className="partner-decision-card"
                            onClick={() => handlePartnerChoiceChange('skip')}
                            disabled={isLoading}
                        >
                            <span className="partner-decision-icon partner-decision-icon-muted" aria-hidden="true">
                                <img src={LightningIcon} alt="" />
                            </span>
                            <span className="partner-decision-body">
                                <span className="partner-decision-title">Ohne eigene Auswahl fortfahren</span>
                                <span className="partner-decision-text">Ich möchte mein Ergebnis direkt erhalten. Um die Partnerauswahl möchte ich mich nicht selbst kümmern.</span>
                                <span className="partner-decision-link partner-decision-link-muted">Zur Auswertung <span aria-hidden="true">→</span></span>
                            </span>
                        </button>
                    </div>
                ) : shouldShowPartnerSelection && partnerChoice === 'select' ? (
                    <div className="partner-selection-actions">
                        <Button
                            variant="outline-light"
                            onClick={handleBackToPartnerDecision}
                            disabled={isLoading}
                        >
                            Zurück zur Auswahl
                        </Button>
                        <Button
                            variant="success btn-next"
                            onClick={handleContinue}
                            disabled={needsPartnerSelection || isLoading}
                        >
                            Jetzt Auswertung starten
                        </Button>
                    </div>
                ) : null}

                {/* Partner Map Section */}
                {shouldShowPartnerSelection && partnerChoice === 'select' && !isLoading && (
                    <div className="partner-map-wrapper" style={{width:'100%' }}>                        
                        <div>
                            <PartnerMap
                                allPartners={partnerLocations}
                                isLoadingPartners={isPartnerLocationsLoading}
                                userLatLng={formValues.Address.LatLng}
                                onPartnerSelect={handlePartnerSelect}
                                selectedPartner={selectedPartner}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Stack>
    )
}

export default SimulationRequest;